"use client";
import useApi from "@/app/hooks/useApi";
import {Button} from "@/components/ui/button";
import {Checkbox} from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Modal, ModalFooter, ModalTitle} from "@/components/ui/modal";
import {cn} from "@/lib/utils";
import {zodResolver} from "@hookform/resolvers/zod";
import {default as Cookies} from "js-cookie";
import {getSession, signIn} from "next-auth/react";
import {useTranslations} from "next-intl";
import Image from "next/image";
import Link from "next/link";
import {redirect, useRouter} from "next/navigation";
import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {z} from "zod";
import {InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot} from "@/components/ui/input-otp";
import {sendGTMEvent} from "@next/third-parties/google";
import Loader from "@/components/Loader";

const Page = () => {
  const api = useApi({});
  const router = useRouter();
  const t = useTranslations("Common");
  const rt = useTranslations("Register");
  const ht = useTranslations("Header");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [verifyId, setVerifyId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("is_forgot_password") === "true") {
      toast.success("password updated successfully", {
        position: "bottom-left",
        hideProgressBar: false,
        autoClose: 2000,
        theme: "colored",
      });
      redirect(`/login`);
    }
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const session = await getSession();
      if (session) {
        if (session?.role === "SuperAdmin") {
          router.push("/admin");
        } else {
          router.replace("/calendar");
        }
      }
    };

    // Pre-fill email if "Remember Me" was selected previously
    const savedEmail = Cookies.get("rememberedEmail");
    if (savedEmail) {
      form.setValue("email", savedEmail);
      setRememberMe(true);
    }

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const formSchema = z.object({
    email: z.string().nonempty({message: "email is required."}),
    password: z.string().nonempty({message: "password is required."}),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const {control, getValues, handleSubmit} = form;

  const otpSchema = z.object({
    pin: z
      .string()
      .min(6, {message: rt("validation.otpLength")})
      .max(6, {message: rt("validation.otpLength")}),
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: {pin: ""}, // Ensures form initializes correctly
  });


  // Handle OTP submission
  const handleOtpSubmit = async (data) => {
    setLoading(true);
    const otp = data.pin;

    if (otp?.length !== 6) {
      toast.error(rt("toast.otpRequired"), {
        position: "bottom-left",
        hideProgressBar: false,
        autoClose: 2000,
        theme: "colored",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await api.post(`/api/companies/verify/${verifyId}`, {
        confirm_code: otp,
      });

      if (!response?.data) {
        setLoading(false);
        return;
      }

      const signInResult = await signIn("credentials", {
        redirect: false,
        email: response?.data?.user?.email,
        password: form.getValues("password"),
      });
      if (signInResult?.error) {
        toast.error(`${rt("toast.loginFailed")}: ${signInResult.error}`, {
          position: "bottom-left",
          hideProgressBar: false,
          autoClose: 2000,
          theme: "colored",
        });
        setLoading(false);
        return;
      }
      sendGTMEvent({event: "register_complete", value: "Register Completed"});
      await router.push("/calendar");
    } catch (error) {
      console.error(rt("toast.invalidOtp"), error);
      setLoading(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post(`/api/resend-otp/${verifyId}`);
      toast.success(response.data.message, {
        position: "bottom-left",
        autoClose: 2000,
        theme: "colored",
      });
    } catch (error) {
      toast.error(rt("toast.otpResendFailed"), {
        position: "bottom-left",
        autoClose: 2000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };


  const emailId = getValues("email");

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const preResponse = await api.post(`/api/login`, {
        email: data.email,
        password: data.password,
      });

      let preResponseData = preResponse.data.data ?? null;

      if (preResponseData && preResponseData?.user_id && (preResponseData?.requires_otp == true)) {
        setLoading(false);
        setVerifyId(preResponseData?.user_id);

        toast.success(`${preResponseData?.message}`, {
          position: "bottom-left",
          hideProgressBar: false,
          autoClose: 3000,
          theme: "colored",
        });

        return;
      }

      const result = await signIn("credentials", {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        toast.error(`Login failed: ${result.error}`, {
          position: "bottom-left",
          hideProgressBar: false,
          autoClose: 2000,
          theme: "colored",
        });
        setLoading(false);
        return;
      }

      // Save email in cookies if "Remember Me" is checked
      if (rememberMe) {
        Cookies.set("rememberedEmail", data.email, {expires: 7}); // Save email for 7 days
      } else {
        Cookies.remove("rememberedEmail");
      }

      const updatedSession = await getSession();
      if (updatedSession?.role === "SuperAdmin") {
        await router.push("/admin");
      } else {
        await router.push("/calendar");
      }
    } catch (error) {
      console.error("Login failed. Please try again.", error);
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (emailId) {
      const response = await api.post("api/forgot-password-mail", {
        email: emailId,
      });

      if (response.data) {
        toast.success(response.data.message, {
          position: "bottom-left",
          hideProgressBar: false,
          autoClose: 2000,
          theme: "colored",
        });
      }
      setIsForgot(false);
    }
  };

  return (
    <>
      <div className="flex min-h-screen flex-1 flex-col px-6 py-12 sm:justify-center lg:px-8">
        {loading && <Loader page="register" />}
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <Link href="/">
            <Image
              width={104}
              height={28}
              src="/images/landingPage/logo.svg"
              alt=""
              className="mx-auto h-10 w-auto justify-center"
            />
          </Link>
          <h2 className="mt-10 text-center text-2xl/9 font-semibold tracking-tight text-gray-900">
            {t("signInPageTitle")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {
            verifyId === null &&
            <Form {...form}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <FormField
                    control={control}
                    name="email"
                    render={({field}) => (
                      <FormItem>
                        <FormLabel>{t("email")}</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage/>
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <div>
                    <FormField
                      control={control}
                      name="password"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>{t("password")}</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage/>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-5 flex items-start">
                    <div className="flex h-5 items-center">
                      <Checkbox
                        id="remember"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked)}
                        className="cursor-pointer"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <Label
                        htmlFor="remember"
                        className="cursor-pointer text-gray-500 dark:text-gray-300"
                      >
                        {t("rememberMe")}
                      </Label>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-6 flex w-full cursor-pointer items-center justify-end"
                    onClick={() => setIsForgot(true)}
                  >
                    <div className="text-sm/6">
                      <p>{t("forgotPasswordLink")}?</p>
                    </div>
                  </button>
                </div>

                <div>
                  <Button
                    type="submit"
                    className="flex w-full justify-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
                        <span className="loader mr-2"></span> {t("processing")}...
                      </>
                    ) : (
                      t("signInButtonText")
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          }
          {
            verifyId !== null &&
            <div className="flex flex-col gap-4">
              <p className="mt-2 text-start text-sm text-gray-600">
                {rt(`description.step3`)}{' '}
                <span className="font-bold">{emailId}</span>
              </p>
              <Form {...otpForm}>
                <form
                  onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={otpForm.control}
                    name="pin"
                    render={({field}) => (
                      <FormItem>
                        <FormControl>
                          <InputOTP
                            id="otp-input"
                            className="flex items-center justify-center text-2xl"
                            maxLength={6}
                            {...field}
                          >
                            <InputOTPGroup>
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={0}
                              />
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={1}
                              />
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={2}
                              />
                            </InputOTPGroup>
                            <InputOTPSeparator className="px-3"/>
                            <InputOTPGroup>
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={3}
                              />
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={4}
                              />
                              <InputOTPSlot
                                className="h-12 w-12 text-xl"
                                index={5}
                              />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                        <FormMessage/>
                      </FormItem>
                    )}
                  />

                  <Button className="w-full" type="submit">
                    {rt("verify")}
                  </Button>

                  <div className="mt-4 flex flex-col items-center justify-between space-y-4 px-8">
                    <div>
                    <span className="text-sm font-normal text-gray-600">
                      {rt("resendPrompt")} &nbsp;
                    </span>
                      <Button
                        variant="ghost"
                        onClick={handleResend}
                        className="hover:bg-background-none text-primary h-full cursor-pointer p-0 text-sm font-medium"
                      >
                        {rt("resend")}
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          }

          {
            verifyId === null &&
            <p className="mt-10 text-center text-sm/6 text-gray-500">
              {t("newToKaboxPrompt")}?
              <Link href="/register" className="ml-1 font-semibold">
                {ht("Create account")}
              </Link>
            </p>
          }
        </div>

        <Modal
          className="m-auto max-w-lg overflow-hidden"
          showModal={isForgot}
          setShowModal={() => setIsForgot(true)}
        >
          <ModalTitle
            title={"Forgot Password"}
            onClose={() => setIsForgot(false)}
          />

          <div
            className={cn(
              "w-full gap-y-6 max-md:overflow-auto",
              "max-md:max-h-[calc(100dvh-200px)]",
              "md:[&>div]:max-h-[calc(100dvh-200px)]",
            )}
          >
            <div
              className="space-y-4 px-6 [-ms-overflow-style:none] [scrollbar-width:none] md:overflow-auto [&::-webkit-scrollbar]:hidden">
              <div>
                <div className="my-6 flex">
                  <Input defaultValue={emailId ? emailId : ""} readOnly/>
                </div>
              </div>
            </div>
          </div>
          <ModalFooter>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsForgot(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={forgotPassword} className="ml-3">
                Send Mail
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default Page;
